import { Component, Inject } from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import {
  MatDialogRef,
  MAT_DIALOG_DATA,
  MatDialogTitle,
  MatDialogContent,
  MatDialogActions,
} from '@angular/material/dialog';
import { OrderCancellationReason } from '@interfaces/orders/1.0';
import { PermissionsService } from '@services/permissions/1.0/permissions.service';
import { ButtonStyle } from '@constants/button/1.0';
import { MessageType } from '@constants/message/1.0';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { NgIf, NgFor } from '@angular/common';
import { MessageComponent } from '../message/1.0/message.component';
import { MatFormField, MatLabel, MatError } from '@angular/material/form-field';
import { MatSelect } from '@angular/material/select';
import { MatOption } from '@angular/material/core';
import { ButtonComponent } from '../button/1.0/button.component';
import { TranslateModule } from '@ngx-translate/core';

export interface CancelOrderDialogData {
  orderIds: string[];
  pendingOrderIds?: string[];
  isCxaOrder: boolean;
  isDeliveryOrder: boolean;
  isBulk: boolean;
  failedOrderIds?: string[];
  successfulOrderIds?: string[];
  showPartialSuccessInfo?: boolean;
  onConfirm?: (cancellationReason: OrderCancellationReason) => void;
}

@Component({
  selector: 'txp-cancel-order-dialog',
  templateUrl: './cancel-order-dialog.component.html',
  styleUrls: ['./cancel-order-dialog.component.scss'],
  standalone: true,
  imports: [
    MatDialogTitle,
    CdkScrollable,
    MatDialogContent,
    NgIf,
    MessageComponent,
    FormsModule,
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    MatSelect,
    NgFor,
    MatOption,
    MatError,
    MatDialogActions,
    ButtonComponent,
    TranslateModule,
  ],
})
export class CancelOrderDialogComponent {
  cancelForm: UntypedFormGroup;
  isLoading = false;
  showError = false;
  availableReasons: { value: OrderCancellationReason; label: string }[] = [];
  buttonStyle = ButtonStyle;
  messageType = MessageType;

  constructor(
    private dialogRef: MatDialogRef<CancelOrderDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: CancelOrderDialogData,
    private formBuilder: UntypedFormBuilder,
    private permissionsService: PermissionsService,
  ) {
    this.initializeForm();
    this.setupAvailableReasons();
  }

  private initializeForm(): void {
    this.cancelForm = this.formBuilder.group({
      cancellationReason: ['', Validators.required],
    });
  }

  private setupAvailableReasons(): void {
    const reasons: { value: OrderCancellationReason; label: string }[] = [
      { value: OrderCancellationReason.CUSTOMER_REQUEST, label: 'Customer Request' },
      { value: OrderCancellationReason.ITEMS_UNAVAILABLE, label: 'Items Unavailable' },
      { value: OrderCancellationReason.KITCHEN_AT_CAPACITY, label: 'Kitchen at Capacity' },
      { value: OrderCancellationReason.STORE_CLOSED, label: 'Store Closed' },
      { value: OrderCancellationReason.DRIVER_DID_NOT_SHOW, label: "Driver didn't show" },
    ];

    // Add test order reason for Tillster Product Operations or Super Admin
    const hasTestOrderPermission = this.permissionsService.getPermission('tenant:write'); // Assuming this covers the required roles
    if (hasTestOrderPermission) {
      reasons.push({ value: OrderCancellationReason.TEST_ORDER, label: 'Test Order' });
    }

    this.availableReasons = reasons;
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  onConfirm(): void {
    if (this.cancelForm.valid) {
      this.isLoading = true;
      const cancellationReason = this.cancelForm.get('cancellationReason')?.value;

      // Call the callback function if provided, otherwise close with the reason
      if (this.data.onConfirm) {
        this.data.onConfirm(cancellationReason);
      } else {
        this.dialogRef.close(cancellationReason);
      }
    }
  }

  get isConfirmDisabled(): boolean {
    return this.cancelForm.invalid || this.isLoading;
  }

  dismissError(): void {
    this.showError = false;
  }

  get orderCount(): number {
    return this.data.orderIds?.length || 0;
  }

  get pendingOrderIds(): string[] {
    return this.data.pendingOrderIds || this.data.orderIds || [];
  }

  get failedOrderIds(): string[] {
    return this.data.failedOrderIds || [];
  }

  get successfulOrderIds(): string[] {
    return this.data.successfulOrderIds || [];
  }

  get isRetryMode(): boolean {
    return this.failedOrderIds.length > 0;
  }

  get confirmationCount(): number {
    return this.isRetryMode ? this.pendingOrderIds.length : this.orderCount;
  }

  get confirmButtonLabelKey(): string {
    return this.isRetryMode ? 'orders.cancelOrderDialog.retryFailed' : 'form.button.confirm';
  }

  get hasFailedOrdersTable(): boolean {
    return this.failedOrderIds.length > 0;
  }

  get errorSummaryKey(): string {
    if (this.hasFailedOrdersTable) {
      return 'orders.cancelOrderDialog.partialFailureSummary';
    }

    return this.data.isBulk && this.orderCount > 1
      ? 'orders.cancelOrderDialog.unableToCancelOrders'
      : 'orders.cancelOrderDialog.unableToCancelOrder';
  }
}
